import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  Typography,
  styled,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../redux/features/authSlice";
import AddTask from "./AddTask";
import { MuiTable } from "../../components/MuiTable";
import { useGetAllTasksQuery } from "../../redux/api/tasksApi";
import { TASK_COLUMN } from "./taskData";
import { useGetAgentsQuery } from "../../redux/api/agentsApi";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useLocation } from "react-router-dom";
import { getDateRange } from "../../components/getDateValues";
import { TASK_STATUSES } from "../../constants/task";
import { DATE_RANGES } from "../../constants/datesList";
import { TaskDetailsModal } from "./TaskDetailsModal";
import EditTask from "./EditTask";

export default function Tasks() {
  const currentAgency = useSelector(selectCurrentagency);
  const { search } = useLocation();
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const queryParams = new URLSearchParams(search);
  const urlStatus = queryParams.get("status");
  const urlRange = queryParams.get("range");
  const urlAgentId = queryParams.get("agentId");
  const taskId = queryParams.get("taskId"); // Get agentId from URL
  const statusId = TASK_STATUSES.find(
    (status) => status.label.toLowerCase() === urlStatus?.toLowerCase()
  )?.value;
  const [filters, setFilters] = useState({
    receiverId: urlAgentId || "", // Initialize with URL agentId if present
    dateRange: urlRange?.toLowerCase().replace("-", "") || "all",
    status: statusId || "",
  });

  const [calculatedDates, setCalculatedDates] = useState({
    from: null,
    to: null,
  });

  const { data: agents } = useGetAgentsQuery(currentAgency.agencyid);

  const { data, isLoading, refetch } = useGetAllTasksQuery({
    agcId: currentAgency.agencyid,
    receiverId: filters.receiverId,
    from: calculatedDates.from,
    to: calculatedDates.to,
    status: filters.status,
  });

  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const formatToISOWithMillis = (date) => {
    if (!date) return null;
    const isoString = date.toISOString();
    return isoString.slice(0, -1) + "000Z";
  };
  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedTask(null);
    refetch();
  };
  useEffect(() => {
    const { fromDate, toDate } = getDateRange(filters.dateRange);
    setCalculatedDates({
      from: formatToISOWithMillis(fromDate),
      to: formatToISOWithMillis(toDate),
    });
  }, [filters.dateRange]);

  // Set the initial agent filter when agents data is loaded
  useEffect(() => {
    if (agents && urlAgentId) {
      // Verify the agent exists in the list
      const agentExists = agents.some((agent) => agent.agentid === urlAgentId);
      if (agentExists) {
        setFilters((prev) => ({
          ...prev,
          receiverId: urlAgentId,
        }));
      }
    }
  }, [agents, urlAgentId]);

  useEffect(() => {
    refetch();
  }, [filters.receiverId, filters.status, calculatedDates, refetch]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleRowClick = (row) => {
    setSelectedTask(row.original); // assuming `row.original` has the full task data
    setDetailsOpen(true);
  };
  const handleEditClick = (task) => {
    setSelectedTask(task);
    setEditOpen(true);
  };

  const handleDetailsClose = () => {
    setSelectedTask(null);
    setDetailsOpen(false);
  };


  const filteredData = React.useMemo(() => {
    if (!data) return [];
    if (taskId) {
      return data.filter(
        (task) => task.taskId?.toString() === taskId.toString()
      );
    }
    return data;
  }, [data, taskId]);

  return (
    <div>
      <StyledTop>
        <Typography variant="h2">All Tasks</Typography>
        <Button
          variant="contained"
          sx={{ ml: "auto", color: "white" }}
          onClick={handleOpen}
        >
          Add Task
        </Button>
      </StyledTop>

      <Box
        sx={{
          mb: 4,
          p: 0,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <FilterAltIcon fontSize="small" />
            Filters
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="agent-filter-label">Assigned To</InputLabel>
                <Select
                  labelId="agent-filter-label"
                  value={filters.receiverId}
                  onChange={(e) =>
                    handleFilterChange("receiverId", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  label="Assigned To"
                >
                  <MenuItem value="">All Agents</MenuItem>
                  {agents
                    ?.filter((agent) => Number(agent.status) === 1) // ✅ Only active agents
                    .map((agent) => (
                      <MenuItem key={agent.agentid} value={agent.agentid}>
                        {agent.agentname}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.dateRange}
                  onChange={(e) =>
                    handleFilterChange("dateRange", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                >
                  {DATE_RANGES.map((range) => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="task-filter-label">Status</InputLabel>
                <Select
                  labelId="task-filter-label"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  variant="outlined"
                  size="small"
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {TASK_STATUSES?.map((task) => (
                    <MenuItem key={task.value} value={task.value}>
                      {task.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <MuiTable
        data={filteredData || []}
        isLoading={isLoading}
        columns={TASK_COLUMN(handleEditClick)}
        enableColor={false}
        enableRowSelection={false}
        enableActionButton={false}
        onRowClick={handleRowClick} // ✅ Added row click handler
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <AddTask onClose={handleClose} />
      </Dialog>
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <EditTask task={selectedTask} onClose={handleEditClose} />
      </Dialog>
      <TaskDetailsModal
        open={detailsOpen}
        onClose={handleDetailsClose}
        task={selectedTask}
      />
    </div>
  );
}

const StyledTop = styled("div")(({ theme }) => ({
  marginTop: "16px",
  marginBottom: "24px",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));
